import { Fragment, Node as PmNode, type Mark } from "prosemirror-model";
import type { Transform } from "prosemirror-transform";
import { textSchema, type Alignment } from "./schema";
import {
  headingLevelToStyle,
  styleToHeadingLevel,
  type TextStyleId,
} from "./typography";

/**
 * Doc/fragment-level normalizers. Everything here operates on ProseMirror
 * nodes (never on raw HTML) and is pure — callers decide when to apply
 * (on load: only structural invariants; on paste: the full spec §2 policy).
 */

/**
 * Spec §4.2 / §13 — deeper lists are flattened, never stored. Nine, as in
 * Google Docs and Word, so their lists paste in whole (it was four).
 */
export const MAX_LIST_DEPTH = 9;

const { nodes, marks } = textSchema;

const isList = (node: PmNode) =>
  node.type === nodes.bullet_list || node.type === nodes.ordered_list;

const mapChildren = (
  fragment: Fragment,
  fn: (node: PmNode, index: number) => PmNode | PmNode[] | null,
): Fragment => {
  const out: PmNode[] = [];
  fragment.forEach((child, _offset, index) => {
    const result = fn(child, index);
    if (result == null) return;
    if (Array.isArray(result)) out.push(...result);
    else out.push(result);
  });
  return Fragment.fromArray(out);
};

// ─── typography helpers ─────────────────────────────────────────────

/** Style a text segment inherits from its block when it has no inline mark. */
export const blockImpliedStyle = (
  block: PmNode,
  listItem: PmNode | null,
): TextStyleId => {
  if (block.type === nodes.heading) {
    return headingLevelToStyle(block.attrs.level as number);
  }
  if (listItem)
    return (listItem.attrs.textStyle as TextStyleId | null) ?? "normal";
  return "normal";
};

const DS_COLOR_VAR_FOR_STYLE: Record<TextStyleId, string> = {
  display: "var(--display-color)",
  "slide-title": "var(--title-color)",
  heading: "var(--heading-color)",
  subheading: "var(--subtitle-color)",
  normal: "var(--paragraph-color)",
};

const stripRedundantMarksInBlock = (
  block: PmNode,
  implied: TextStyleId,
): PmNode => {
  const impliedColor = DS_COLOR_VAR_FOR_STYLE[implied].replace(/\s/g, "");
  const content = mapChildren(block.content, (inline) => {
    const kept = inline.marks.filter((mark: Mark) => {
      // The parser applies an element's own styles to atoms regardless of the
      // node's `marks` spec (e.g. font-size on an icon) — enforce it here.
      // (Text nodes have no own mark set — their parent decides.)
      if (
        inline.type.spec.marks != null &&
        !inline.type.allowsMarkType(mark.type)
      ) {
        return false;
      }
      if (mark.type === marks.text_style) return mark.attrs.style !== implied;
      if (mark.type === marks.color) {
        return String(mark.attrs.color).replace(/\s/g, "") !== impliedColor;
      }
      return true;
    });
    return kept.length === inline.marks.length ? inline : inline.mark(kept);
  });
  return block.copy(content);
};

/**
 * Legacy Froala content stores block typography twice: as the tag AND as
 * inline `font-size: var(--title-size); color: var(--title-color)` on the same
 * element, which the parser turns into `text_style`/`color` marks on every
 * segment. Marks that merely restate the enclosing block's style are noise —
 * drop them so the doc has one source of truth per segment.
 */
export const stripRedundantTypographyMarks = (doc: PmNode): PmNode => {
  const walk = (node: PmNode, listItem: PmNode | null): PmNode => {
    if (node.isTextblock) {
      return stripRedundantMarksInBlock(
        node,
        blockImpliedStyle(node, listItem),
      );
    }
    if (node.type === nodes.list_item) {
      return node.copy(mapChildren(node.content, (child) => walk(child, node)));
    }
    return node.copy(mapChildren(node.content, (child) => walk(child, null)));
  };
  return walk(doc, null);
};

// ─── list depth ─────────────────────────────────────────────────────

/**
 * Flatten lists nested deeper than MAX_LIST_DEPTH: a list at depth 5 has its
 * items hoisted into the depth-4 list right after the item that held them.
 */
export const capListDepth = (
  doc: PmNode,
  maxDepth = MAX_LIST_DEPTH,
): PmNode => {
  const processList = (list: PmNode, depth: number): PmNode => {
    const items: PmNode[] = [];
    list.content.forEach((item) => {
      const kept: PmNode[] = [];
      const hoisted: PmNode[] = [];
      item.content.forEach((block) => {
        if (!isList(block)) {
          kept.push(walk(block));
          return;
        }
        if (depth + 1 > maxDepth) {
          // Too deep: flatten the whole subtree to this level.
          const flat = processList(block, maxDepth);
          flat.content.forEach((li) => hoisted.push(li));
        } else {
          kept.push(processList(block, depth + 1));
        }
      });
      items.push(item.copy(Fragment.fromArray(kept)), ...hoisted);
    });
    return list.copy(Fragment.fromArray(items));
  };

  const walk = (node: PmNode): PmNode => {
    if (isList(node)) return processList(node, 1);
    if (node.isTextblock || node.isLeaf) return node;
    return node.copy(mapChildren(node.content, walk));
  };

  return walk(doc);
};

/** Depth (1-based) of the innermost list around a resolved position, 0 if none. */
export const listDepthAt = (doc: PmNode, pos: number): number => {
  const $pos = doc.resolve(pos);
  let depth = 0;
  for (let d = $pos.depth; d > 0; d--) {
    if (isList($pos.node(d))) depth++;
  }
  return depth;
};

/** Deepest list nesting anywhere inside a fragment (for indent guards). */
export const maxListDepthIn = (fragment: Fragment, base = 0): number => {
  let max = base;
  fragment.forEach((node) => {
    if (isList(node)) {
      max = Math.max(max, maxListDepthIn(node.content, base + 1));
    } else if (!node.isLeaf) {
      max = Math.max(max, maxListDepthIn(node.content, base));
    }
  });
  return max;
};

// ─── paste-only policy (spec §2, §3.6) ──────────────────────────────

const firstSegmentStyle = (block: PmNode): TextStyleId | null => {
  let found: TextStyleId | null = null;
  block.content.forEach((inline) => {
    if (found) return;
    const mark = marks.text_style.isInSet(inline.marks);
    if (mark) found = mark.attrs.style as TextStyleId;
  });
  return found;
};

const withoutTextStyleMarks = (block: PmNode): PmNode =>
  block.copy(
    mapChildren(block.content, (inline) =>
      marks.text_style.isInSet(inline.marks)
        ? inline.mark(marks.text_style.removeFromSet(inline.marks))
        : inline,
    ),
  );

/**
 * Spec §3.6 — a block with mixed source sizes is normalized to ONE style,
 * taken from its first text segment, and that style is applied to the whole
 * block: headings/paragraphs are re-tagged, list items get `textStyle`. Inline
 * `text_style` marks never survive a paste; typography arrives block-level.
 */
/**
 * External headings by tag, one level down from Storydoc's own tags: a page's
 * <h1> is its main heading, Storydoc's Title — the size a Google Docs
 * Heading 1 (20pt) maps to, so a Doc and a Notion page paste alike (QA E4).
 */
const EXTERNAL_HEADING_STYLE: Record<number, TextStyleId> = {
  1: "slide-title",
  2: "heading",
  3: "subheading",
  4: "subheading",
};

/**
 * Body text on the web is 16–20px; below the Heading band a paragraph stays
 * Normal text rather than becoming a Subheading (QA E3 — an 18px blog
 * paragraph pasted as Subheading). Headings keep the full band table.
 */
const externalParagraphStyle = (style: TextStyleId): TextStyleId =>
  style === "subheading" ? "normal" : style;

export const normalizePastedTypography = (
  fragment: Fragment,
  { external = false }: { external?: boolean } = {},
): Fragment => {
  const impliedStyle = (block: PmNode, item: PmNode | null): TextStyleId => {
    const sized = firstSegmentStyle(block);
    if (!external) return sized ?? blockImpliedStyle(block, item);
    if (block.type === nodes.heading) {
      return (
        sized ?? EXTERNAL_HEADING_STYLE[block.attrs.level as number] ?? "normal"
      );
    }
    return externalParagraphStyle(sized ?? blockImpliedStyle(block, item));
  };
  const walk = (node: PmNode): PmNode => {
    if (node.type === nodes.list_item) {
      let itemStyle = (node.attrs.textStyle as TextStyleId | null) ?? null;
      const content = mapChildren(node.content, (child, index) => {
        if (child.isTextblock) {
          const style = impliedStyle(child, node);
          if (index === 0) itemStyle = style;
          // Items hold paragraphs; the style lives on the item.
          return nodes.paragraph.create(
            { align: child.attrs.align ?? null },
            withoutTextStyleMarks(child).content,
          );
        }
        return walk(child);
      });
      return node.type.create(
        {
          ...node.attrs,
          textStyle: itemStyle === "normal" ? null : itemStyle,
          // Spec §2.1: line height / spacing / animation do not survive a paste.
          lineHeight: null,
          direction: null,
          animation: null,
        },
        content,
      );
    }
    if (node.isTextblock) {
      const style = impliedStyle(node, null);
      const clean = withoutTextStyleMarks(node);
      const level = styleToHeadingLevel(style);
      return level
        ? nodes.heading.create(
            { level, align: node.attrs.align ?? null },
            clean.content,
          )
        : nodes.paragraph.create(
            { align: node.attrs.align ?? null },
            clean.content,
          );
    }
    if (node.isLeaf) return node;
    return node.copy(mapChildren(node.content, walk));
  };
  return mapChildren(fragment, walk);
};

const isEmptyTextblock = (node: PmNode) =>
  node.isTextblock && node.textContent.trim() === "" && !hasAtom(node);

/** An inline atom other than a line break: a counter, icon, emoji, variable. */
const isInlineAtom = (node: PmNode) =>
  // Text is a leaf, so ProseMirror counts it as an atom too: every block with
  // a character in it — Notion's one-space paragraph included — "had an atom"
  // and was never treated as blank (product QA E4).
  node.isAtom && !node.isText && node.type !== nodes.hard_break;

const hasAtom = (node: PmNode) => {
  let atom = false;
  node.content.forEach((child) => {
    if (isInlineAtom(child)) atom = true;
  });
  return atom;
};

/**
 * Whitespace from the source HTML's own formatting (the newline between two
 * tags), never content. The parser leaves it as a bare top-level text node or
 * as a paragraph of its own.
 */
const isFormattingWhitespace = (node: PmNode) =>
  (node.isText ||
    (node.isTextblock && node.childCount > 0 && !hasAtom(node))) &&
  /^[ \t\r\n]+$/.test(node.textContent);

/**
 * Pasted spacing (QA E3): a blank line the author put BETWEEN blocks (an empty
 * paragraph, &nbsp;, <br>) is kept; blank lines at the very start or end, and
 * paragraphs made only of the source HTML's own formatting whitespace, go.
 * Inside list items blank paragraphs still go.
 */
export const dropEmptyParagraphs = (fragment: Fragment): Fragment => {
  // A paste within one line (Notion's inline formatting) is bare text runs:
  // a space between two formatted words is text, not the whitespace between
  // two blocks, and there are no blank lines to drop (product QA E4).
  let inlineOnly = true;
  fragment.forEach((child) => {
    if (!child.isInline) inlineOnly = false;
  });
  if (inlineOnly) return fragment;
  const walk = (node: PmNode): PmNode =>
    node.isTextblock || node.isLeaf
      ? node
      : node.copy(
          mapChildren(node.content, (child) =>
            isEmptyTextblock(child) && node.childCount > 1 ? null : walk(child),
          ),
        );
  const top: PmNode[] = [];
  fragment.forEach((child) => {
    if (!isFormattingWhitespace(child)) top.push(walk(child));
  });
  while (top.length > 1 && isEmptyTextblock(top[0])) top.shift();
  while (top.length > 1 && isEmptyTextblock(top[top.length - 1])) top.pop();
  return top.length === 0 ? fragment : Fragment.fromArray(top);
};

/**
 * A `<br>` at the end of a block draws nothing (Froala's empty `<p><br></p>`,
 * pasted `<li>text<br></li>`), but as a hard break it keeps the block from
 * being empty — Enter in such a list item never exited the list (QA E3).
 */
export const stripTrailingBreaks = (fragment: Fragment): Fragment => {
  const walk = (node: PmNode): PmNode => {
    if (node.isTextblock) {
      let content = node.content;
      while (content.lastChild?.type === nodes.hard_break) {
        content = content.cut(0, content.size - content.lastChild.nodeSize);
      }
      return content === node.content ? node : node.copy(content);
    }
    return node.isLeaf ? node : node.copy(mapChildren(node.content, walk));
  };
  return mapChildren(fragment, walk);
};

/**
 * Word and some web pages put every list item in a list of its own, so each
 * one numbered "1." (QA E2). Adjacent top-level lists of the same kind are one.
 */
export const mergeAdjacentLists = (fragment: Fragment): Fragment => {
  const out: PmNode[] = [];
  fragment.forEach((child) => {
    const prev = out[out.length - 1];
    if (
      prev &&
      isList(child) &&
      prev.type === child.type &&
      prev.attrs.kind === child.attrs.kind &&
      prev.attrs.iconName === child.attrs.iconName
    ) {
      out[out.length - 1] = prev.copy(prev.content.append(child.content));
    } else {
      out.push(child);
    }
  });
  return Fragment.fromArray(out);
};

/**
 * Pasted blocks with no explicit alignment default to `left` (Froala parity:
 * the element otherwise inherits the layout's centre alignment). Plain-text
 * pastes deliberately skip this — see paste.ts and spec §2.3.
 */
export const applyDefaultAlignment = (
  fragment: Fragment,
  align: Alignment,
): Fragment => {
  const walk = (node: PmNode, inItem: boolean): PmNode => {
    if (node.isLeaf) return node;
    const isItem = node.type === nodes.list_item;
    const content = mapChildren(node.content, (child) => walk(child, isItem));
    // Blocks inside an item inherit the item's alignment; only the item gets it.
    if (node.isTextblock && inItem) return node.copy(content);
    if ((node.isTextblock || isItem) && node.attrs.align == null) {
      return node.type.create({ ...node.attrs, align }, content, node.marks);
    }
    return node.copy(content);
  };
  return mapChildren(fragment, (child) => walk(child, false));
};

/**
 * Spec §2.4 — content pasted into a list becomes list items: every top-level
 * textblock is wrapped in an item of the surrounding list's type, and pasted
 * lists contribute their items directly. Plain-text lines carry nesting via
 * leading whitespace (two spaces or a tab per level) which is honoured up to
 * MAX_LIST_DEPTH.
 */
export const blocksToListItems = (
  fragment: Fragment,
  listType: typeof nodes.bullet_list | typeof nodes.ordered_list,
  listAttrs: Record<string, unknown>,
  itemAttrs: Record<string, unknown>,
  detectIndent: boolean,
): PmNode => {
  type Entry = { level: number; item: PmNode };
  const entries: Entry[] = [];

  fragment.forEach((block) => {
    if (isList(block)) {
      block.content.forEach((item) => entries.push({ level: 0, item }));
      return;
    }
    if (!block.isTextblock) return;
    // Blank lines don't become empty items.
    if (block.content.size === 0 || block.textContent.trim() === "") {
      let hasAtom = false;
      block.content.forEach((child) => {
        if (isInlineAtom(child)) hasAtom = true;
      });
      if (!hasAtom) return;
    }
    let level = 0;
    let content = block.content;
    if (detectIndent && block.firstChild?.isText) {
      const text = block.firstChild.text ?? "";
      // Text copied from a page or a notes app indents with &nbsp; (QA E7).
      const leading = text.match(/^[\t \u00a0]*/)?.[0] ?? "";
      level = Math.min(
        MAX_LIST_DEPTH - 1,
        leading.split("").reduce((n, c) => n + (c === "\t" ? 1 : 0.5), 0) | 0,
      );
      if (leading) {
        const rest = text.slice(leading.length);
        const first = rest
          ? textSchema.text(rest, block.firstChild.marks)
          : null;
        const others: PmNode[] = [];
        content.forEach((child, _o, i) => {
          if (i > 0) others.push(child);
        });
        content = Fragment.fromArray(first ? [first, ...others] : others);
      }
    }
    // Block-level typography/alignment of the pasted block move to the item.
    const blockStyle = blockImpliedStyle(block, null);
    entries.push({
      level,
      item: nodes.list_item.create(
        {
          ...itemAttrs,
          textStyle:
            blockStyle === "normal"
              ? (itemAttrs.textStyle ?? null)
              : blockStyle,
          align: block.attrs.align ?? itemAttrs.align ?? null,
        },
        nodes.paragraph.create(null, content),
      ),
    });
  });

  // Build the tree from levels: a deeper entry nests under the previous item.
  const build = (start: number, level: number): [PmNode[], number] => {
    const items: PmNode[] = [];
    let i = start;
    while (i < entries.length && entries[i].level >= level) {
      if (entries[i].level > level) {
        // Orphan indent (no parent item yet) — treat as this level.
        entries[i] = { ...entries[i], level };
      }
      let item = entries[i].item;
      i++;
      if (i < entries.length && entries[i].level > level) {
        const [children, next] = build(i, level + 1);
        i = next;
        item = item.copy(
          item.content.append(
            Fragment.from(
              listType.create(listAttrs, Fragment.fromArray(children)),
            ),
          ),
        );
      }
      items.push(item);
    }
    return [items, i];
  };

  const [items] = build(0, 0);
  const list = listType.create(
    listAttrs,
    Fragment.fromArray(
      items.length
        ? items
        : [nodes.list_item.create(itemAttrs, nodes.paragraph.create())],
    ),
  );
  return capListDepth(list);
};

/** Text and variable pills — what reads as words (pills carry marks too). */
const isWordLike = (node: PmNode) =>
  node.isText || node.type === nodes.variable;

/** Colour of the item's first word — the text right after the marker. */
const firstTextColor = (item: PmNode): string | null => {
  let color: string | null | undefined;
  item.descendants((node) => {
    // Nested lists have markers of their own.
    if (color !== undefined || isList(node)) return false;
    if (!isWordLike(node)) return true;
    color = (marks.color.isInSet(node.marks)?.attrs.color as string) ?? null;
    return false;
  });
  return color ?? null;
};

/**
 * When every character of a text block has an explicit size, the largest of
 * them — the block then sizes its line box to its tallest text instead of the
 * style's default. Any unsized text keeps the block at its style size (null).
 */
const explicitBlockFontSize = (block: PmNode): number | null => {
  let px: number | null | undefined;
  block.forEach((inline) => {
    if (!isWordLike(inline) || px === null) return;
    const own =
      (marks.font_size.isInSet(inline.marks)?.attrs.px as number) ?? null;
    px = own === null ? null : Math.max(px ?? 0, own);
  });
  return px ?? null;
};

/**
 * Attributes that are a function of the text, recomputed on every change so
 * colouring, resizing, clearing, pasting and typing all stay in sync:
 * - list item `markerColor` — the marker takes its first word's colour (§4);
 * - text block `fontSize` — when all its text is explicitly sized, the block
 *   is sized too, so its line height follows (an H1 set to 12px is 12px tall).
 */
export const applyDerivedAttrs = <T extends Transform>(tr: T): T => {
  tr.doc.descendants((node, pos) => {
    let patch: Record<string, unknown> | null = null;
    if (node.type === nodes.list_item) {
      const markerColor = firstTextColor(node);
      if (node.attrs.markerColor !== markerColor) patch = { markerColor };
    } else if (node.isTextblock) {
      const fontSize = explicitBlockFontSize(node);
      if (node.attrs.fontSize !== fontSize) patch = { fontSize };
    }
    if (patch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch });
    return !node.isTextblock;
  });
  return tr;
};
